import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Flag } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

/**
 * Phase 4 plan 04-05: tiny hook — true iff ANY inheritable field on an LLM
 * node has `inherited=false`, or a model_specific entry exists (which is
 * per-node by construction — CONTEXT D-18). Memoised on the config reference.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook + component live in same module by design
export function useHasOverride(
  config: Record<string, unknown> | null | undefined
): boolean {
  return useMemo(() => {
    if (!config) return false
    const topLevel = ['model', 'temperature', 'max_tokens'] as const
    for (const k of topLevel) {
      const entry = (config as Record<string, unknown>)[k] as
        | { inherited?: boolean }
        | undefined
      if (entry && entry.inherited === false) return true
    }
    const advanced = (config as { advanced?: Record<string, unknown> })
      .advanced
    if (advanced) {
      for (const entry of Object.values(advanced) as Array<{
        inherited?: boolean
      } | null>) {
        if (entry && entry.inherited === false) return true
      }
    }
    const modelSpecific = (config as { model_specific?: Record<string, unknown> })
      .model_specific
    if (modelSpecific && Object.keys(modelSpecific).length > 0) return true
    return false
  }, [config])
}

export interface OverrideCanvasBadgeProps {
  config: Record<string, unknown> | null | undefined
}

/**
 * Render amber "Override" badge (D-07) when any field is overridden.
 * Positioned `absolute top-1 right-1` inside the node container by the caller,
 * or can be dropped standalone (no positioning assumed here so tests can mount
 * without a positioned parent). Consumers inside React Flow node templates
 * should wrap this in their own `relative` container.
 */
export function OverrideCanvasBadge({ config }: OverrideCanvasBadgeProps) {
  const { t } = useTranslation('workflows')
  const hasOverride = useHasOverride(config)
  if (!hasOverride) return null
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="warning"
            className="inline-flex items-center gap-1"
            aria-label={t('inheritance.overrideBadge')}
          >
            <Flag className="h-3 w-3" />
            {t('inheritance.overrideBadge')}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          {t('inheritance.overrideBadgeTooltip')}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
